import api from './api';

export interface ResponsavelInfo {
  id: number;
  nome: string;
  cpfCnpj?: string;
  email?: string;
  telefone?: string;
}

export interface Aluno {
  id: number;
  matricula: string;
  nome: string;
  dataNascimento: string;
  cpf?: string;
  rg?: string;
  endereco?: string;
  telefone?: string;
  email?: string;
  ativo: boolean;
  valorMensalidadeCustomizado?: number;
  percentualDesconto: number;
  valorDesconto: number;
  observacoesMensalidade?: string;
  turmaId?: number;
  responsaveis?: ResponsavelInfo[];
  criadoEm: string;
  atualizadoEm: string;
}

export interface CreateAlunoDto {
  nome: string;
  matricula: string;
  dataNascimento: string;
  cpf?: string;
  rg?: string;
  endereco?: string;
  telefone?: string;
  email?: string;
  turmaId: number;
  responsavelFinanceiroId?: number;
  valorMensalidadeCustomizado?: number;
}

export interface UpdateAlunoDto {
  nome?: string;
  matricula?: string;
  dataNascimento?: string;
  cpf?: string;
  rg?: string;
  endereco?: string;
  telefone?: string;
  email?: string;
  turmaId?: number;
  responsavelFinanceiroId?: number;
  valorMensalidadeCustomizado?: number;
  ativo?: boolean;
}

type RawAluno = {
  id: number;
  matricula: string;
  nome: string;
  dataNascimento?: string;
  data_nascimento?: string;
  cpf?: string;
  rg?: string;
  endereco?: string;
  telefone?: string;
  email?: string;
  ativo: boolean;
  valorMensalidadeCustomizado?: number;
  valor_mensalidade_customizado?: number;
  percentualDesconto?: number;
  percentual_desconto?: number;
  valorDesconto?: number;
  valor_desconto?: number;
  observacoesMensalidade?: string;
  observacoes_mensalidade?: string;
  turmaId?: number;
  turma_id?: number;
  responsaveis?: ResponsavelInfo[];
  criadoEm?: string;
  criado_em?: string;
  atualizadoEm?: string;
  atualizado_em?: string;
};

const normalizeAluno = (raw: RawAluno): Aluno => ({
  id: raw.id,
  matricula: raw.matricula,
  nome: raw.nome,
  dataNascimento: raw.dataNascimento ?? raw.data_nascimento ?? '',
  cpf: raw.cpf,
  rg: raw.rg,
  endereco: raw.endereco,
  telefone: raw.telefone,
  email: raw.email,
  ativo: raw.ativo,
  valorMensalidadeCustomizado: raw.valorMensalidadeCustomizado ?? raw.valor_mensalidade_customizado,
  percentualDesconto: raw.percentualDesconto ?? raw.percentual_desconto ?? 0,
  valorDesconto: raw.valorDesconto ?? raw.valor_desconto ?? 0,
  observacoesMensalidade: raw.observacoesMensalidade ?? raw.observacoes_mensalidade,
  turmaId: raw.turmaId ?? raw.turma_id,
  responsaveis: raw.responsaveis,
  criadoEm: raw.criadoEm ?? raw.criado_em ?? new Date().toISOString(),
  atualizadoEm: raw.atualizadoEm ?? raw.atualizado_em ?? new Date().toISOString(),
});

export const alunosService = {
  // Listar todos os alunos
  async getAll(): Promise<Aluno[]> {
    const response = await api.get<RawAluno[]>('/alunos');
    return response.data.map(normalizeAluno);
  },

  // Buscar aluno por ID
  async getById(id: number): Promise<Aluno> {
    const response = await api.get<RawAluno>(`/alunos/${id}`);
    return normalizeAluno(response.data);
  },

  // Listar alunos por turma
  async getByTurma(turmaId: number): Promise<Aluno[]> {
    const response = await api.get<RawAluno[]>(`/alunos/turma/${turmaId}`);
    return response.data.map(normalizeAluno);
  },

  // Listar alunos por responsável
  async getByResponsavel(responsavelId: number): Promise<Aluno[]> {
    const response = await api.get<RawAluno[]>(`/alunos/responsavel/${responsavelId}`);
    return response.data.map(normalizeAluno);
  },

  // Listar alunos sem contrato em um ano letivo
  async getSemContrato(anoLetivoId = 1): Promise<Aluno[]> {
    const response = await api.get<RawAluno[]>(`/alunos/sem-contrato?anoLetivoId=${anoLetivoId}`);
    return response.data.map(normalizeAluno);
  },

  // Criar novo aluno
  async create(data: CreateAlunoDto): Promise<Aluno> {
    const response = await api.post<RawAluno>('/alunos', data);
    return normalizeAluno(response.data);
  },

  // Atualizar aluno
  async update(id: number, data: UpdateAlunoDto): Promise<Aluno> {
    const response = await api.patch<RawAluno>(`/alunos/${id}`, data);
    return normalizeAluno(response.data);
  },

  // Ativar/Desativar aluno
  async toggleAtivo(id: number): Promise<Aluno> {
    const response = await api.patch<RawAluno>(`/alunos/${id}/toggle-ativo`);
    return normalizeAluno(response.data);
  },

  // Remover aluno
  async delete(id: number): Promise<void> {
    await api.delete(`/alunos/${id}`);
  },

  // Atualizar mensalidade customizada
  async updateMensalidade(id: number, data: { valorMensalidadeCustomizado?: number; observacoesMensalidade?: string }): Promise<Aluno> {
    const response = await api.patch<RawAluno>(`/alunos/${id}/mensalidade`, data);
    return normalizeAluno(response.data);
  },
};
